import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiChevronDown, FiUser, FiLogOut, FiCalendar, FiGrid, FiSettings, FiMenu, FiX, FiArrowRight, FiBell } from 'react-icons/fi';

import { useAuth } from '../../context/AuthContext';
import { getColaboradores, getSettings } from '../../services/api';
import './Header.css';

const Header = () => {
  const { isAuthenticated, user, isAdmin, isColaborador, isPaciente, logout, displayName, initials } = useAuth();
  const isLoggedIn = isAuthenticated;
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const [fallbackImage, setFallbackImage] = useState(null);
  const [logoUrl, setLogoUrl] = useState('/images/logo-crema.png');
  const [siteName, setSiteName] = useState('IntegraMente HwH');

  useEffect(() => {
    getSettings().then(res => {
      if (res.data?.site_logo) setLogoUrl(res.data.site_logo);
      if (res.data?.site_name) setSiteName(res.data.site_name);
    }).catch(err => console.error("Error loading logo", err));
  }, []);

  // Independent fetch for collaborator image if missing in auth context
  useEffect(() => {
    let isMounted = true;
    const fetchCollabImage = async () => {
      if (user?.role === 'colaborador' && !user.imagen_url && !fallbackImage) {
        try {
          const { data: colabs } = await getColaboradores({ activo: 1 });
          const list = Array.isArray(colabs) ? colabs : (colabs?.data || []);

          const norm = (s) => (s || '').toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
          const meId = String(user.id || '');
          const meEmail = norm(user.email);
          const meFull = norm(`${user.nombre} ${user.apellido}`).replace(/\s+/g, ' ');

          const found = list.find(c => {
            const cId = String(c.usuario_id || '');
            if (cId && meId && cId === meId) return true;

            const cEmail = norm(c.email);
            if (cEmail && meEmail && cEmail === meEmail) return true;

            const cFull = norm(`${c.nombre} ${c.apellido}`).replace(/\s+/g, ' ');
            if (meFull && cFull) {
              if (cFull.includes(meFull) || meFull.includes(cFull)) return true;
            }
            return false;
          });

          if (found && isMounted) {
            const img = found.imagen_url || found.imagen || found.foto || found.avatar;
            if (img) setFallbackImage(img);
          }
        } catch (error) {
          console.error("Failed to fetch header avatar fallback", error);
        }
      }
    };

    fetchCollabImage();
    return () => { isMounted = false; };
  }, [user, fallbackImage]);

  const avatarUrl = user?.imagen_url || fallbackImage;

  const isMiembro = user?.role === 'miembro';

  const profilePath = isAdmin ? '/admin/mi-cuenta' : isColaborador ? '/colaborador/mi-cuenta' : isMiembro ? '/miembro/mi-perfil' : '/usuario/mi-cuenta';

  const panelLink = isAdmin
    ? { to: '/admin', label: 'Panel admin', tone: 'bg-orange-500/10 text-orange-600' }
    : isColaborador
      ? { to: '/colaborador', label: 'Panel colaborador', tone: 'bg-purple-500/10 text-purple-600' }
      : isMiembro
        ? { to: '/miembro', label: 'Panel miembro', tone: 'bg-teal-500/10 text-teal-600' }
        : { to: '/user', label: 'Mi panel', tone: 'bg-blue-500/10 text-blue-600' };

  // Navigation links configuration - clean and simple
  const navigationLinks = [
    { name: 'Inicio', href: '/' },
    { name: 'Equipo', href: '/equipo' },
    { name: 'Contacto', href: '/contacto' },
  ];

  // Scroll behavior for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    const path = location.pathname || '';
    const isProtected = path.startsWith('/admin') || path.startsWith('/colaborador') || path.startsWith('/user') || path.startsWith('/my-appointments');
    if (isProtected) {
      navigate('/login', { replace: true });
    }
  };

  // displayName and initials now provided by AuthContext for consistent formatting

  return (
    <header className={`header ${scrolled ? 'header-scrolled' : ''}`}>
      <div className="header-container">
        {/* Brand Section */}
        <Link to="/" className="brand">
          <img className="brand-logo" src={logoUrl} alt={siteName} />
          <span className="brand-text flex flex-col justify-center leading-none ml-2">
            <span className="text-[16px] sm:text-[20px] font-bold tracking-tight text-slate-900 mb-1">IntegraMente</span>
            <span className="text-[11px] sm:text-[14px] font-semibold tracking-wide text-blue-600">Wellness & Health</span>
          </span>
        </Link>

        {/* Navigation Links - Desktop */}
        <nav className="nav-links">
          {navigationLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`nav-link ${location.pathname === link.href ? 'active' : ''}`}
            >
              {link.name}
              {link.badge && link.badge > 0 && (
                <span className="nav-badge">{link.badge}</span>
              )}
            </Link>
          ))}
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <FiX /> : <FiMenu />}
        </button>

        {/* Auth Section */}
        <div className="auth-section">
          {/* Utility Icons - Search removed */}

          {isLoggedIn && (
            <button
              className="icon-btn icon-btn-notification"
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              aria-label="Notificaciones"
            >
              <FiBell />
              {user?.unreadNotifications > 0 && (
                <span className="notification-dot">{user.unreadNotifications}</span>
              )}
            </button>
          )}

          {!isLoggedIn ? (
            <div className="auth-buttons flex items-center gap-2">
              <Link to="/login" className="btn btn-ghost !p-2 sm:!px-4 order-2 sm:order-1 !rounded-full sm:!rounded-md">
                <FiUser className="h-4 w-4 sm:hidden text-slate-700" />
                <span className="hidden sm:inline">Iniciar Sesión</span>
              </Link>
              <Link to="/reservar/servicios" className="btn btn-primary !py-1.5 !px-3 sm:!px-4 order-1 sm:order-2 !rounded-full sm:!rounded-md">
                <span className="hidden sm:inline">Reservar Cita</span>
                <span className="sm:hidden text-[11px] font-bold uppercase tracking-wider">Reservar</span>
                <FiArrowRight className="btn-icon hidden sm:inline" />
              </Link>
            </div>
          ) : (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                aria-label="Abrir menú de usuario"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 pl-1 pr-3 text-sm font-medium text-slate-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={displayName}
                    className="h-9 w-9 rounded-full object-cover shadow-sm ring-1 ring-slate-200"
                  />
                ) : (
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-sm font-semibold text-white shadow-inner">
                    {(initials || 'U').toUpperCase()}
                  </span>
                )}
                <span className="hidden max-w-[140px] truncate text-left text-sm font-semibold text-slate-900 sm:block">
                  {displayName}
                </span>
                <FiChevronDown
                  className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${menuOpen ? 'rotate-180 text-blue-600' : ''}`}
                />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-[1400] mt-3 w-72 origin-top-right rounded-3xl border border-slate-200 bg-white p-3 shadow-2xl ring-1 ring-black/5 focus:outline-none"
                >
                  <div className="px-3 py-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Sesión Activa</p>
                    <p className="mt-1 truncate text-base font-bold text-slate-900">{displayName}</p>
                    <p className="truncate text-sm font-medium text-slate-500">{user?.email || ''}</p>
                  </div>

                  <div className="mt-2 space-y-1">
                    <Link
                      to={panelLink.to}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                      className="group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      <span className={`flex h-10 w-10 items-center justify-center rounded-xl bg-opacity-10 transition group-hover:bg-opacity-20 ${panelLink.tone.replace('bg-', 'bg-').replace('text-', 'text-')}`}>
                        <FiGrid className="h-5 w-5" />
                      </span>
                      <span className="flex-1 font-semibold">{panelLink.label}</span>
                    </Link>

                    <Link
                      to={isPaciente ? '/usuario/configuracion' : profilePath}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                      className="group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-100">
                        {isPaciente ? <FiSettings className="h-5 w-5" /> : <FiUser className="h-5 w-5" />}
                      </span>
                      <span className="flex-1 font-semibold">Mi perfil</span>
                    </Link>

                    <div className="my-2 h-px bg-slate-100 mx-3" />

                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      className="group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium text-red-600 transition hover:bg-red-50"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-600 transition group-hover:bg-red-100">
                        <FiLogOut className="h-5 w-5" />
                      </span>
                      <span className="flex-1 font-bold">Cerrar sesión</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
          <div className="mobile-menu">
            <div className="mobile-menu-header">
              <span className="mobile-menu-title">Menú</span>
              <button
                className="mobile-menu-close"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Cerrar menú"
              >
                <FiX />
              </button>
            </div>
            <nav className="mobile-nav">
              {navigationLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`mobile-nav-link ${location.pathname === link.href ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                  {link.badge && link.badge > 0 && (
                    <span className="nav-badge">{link.badge}</span>
                  )}
                </Link>
              ))}
            </nav>
            {!isLoggedIn && (
              <div className="mobile-auth-buttons">
                <Link to="/login" className="btn btn-ghost" onClick={() => setMobileMenuOpen(false)}>
                  Iniciar Sesión
                </Link>
                <Link to="/reservar/servicios" className="btn btn-primary" onClick={() => setMobileMenuOpen(false)}>
                  Reservar Cita
                  <FiArrowRight className="btn-icon" />
                </Link>
              </div>
            )}
          </div>
        </>
      )}


      {/* Notifications Panel */}
      {notificationsOpen && isLoggedIn && (
        <>
          <div className="notifications-overlay" onClick={() => setNotificationsOpen(false)} />
          <div className="notifications-panel">
            <div className="notifications-header">
              <h3>Notificaciones</h3>
              <button
                className="notifications-close"
                onClick={() => setNotificationsOpen(false)}
                aria-label="Cerrar notificaciones"
              >
                <FiX />
              </button>
            </div>
            <div className="notifications-content">
              {user?.notifications && user.notifications.length > 0 ? (
                user.notifications.map((notification, index) => (
                  <div key={index} className={`notification-item ${notification.read ? 'read' : 'unread'}`}>
                    <div className="notification-icon">
                      {notification.type === 'appointment' && <FiCalendar />}
                      {notification.type === 'message' && <FiUser />}
                      {notification.type === 'reminder' && <FiBell />}
                    </div>
                    <div className="notification-content">
                      <p className="notification-title">{notification.title}</p>
                      <p className="notification-message">{notification.message}</p>
                      <span className="notification-time">{notification.time}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="notifications-empty">
                  <FiBell className="empty-icon" />
                  <p>No tienes notificaciones</p>
                </div>
              )}
            </div>
            {user?.notifications && user.notifications.length > 0 && (
              <div className="notifications-footer">
                <button className="btn-text" onClick={() => navigate('/notificaciones')}>
                  Ver todas las notificaciones
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
